import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FONTS, COLORS } from "../utils/styleConstants";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SigningIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #ff7057;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
`;

const ModalTitle = styled.h2`
  color: ${COLORS.PRIMARY};
  font-family: ${FONTS.PRIMARY};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem 0;
  text-align: center;
`;

const ModalMessage = styled.p`
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-size: 1rem;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-family: ${FONTS.SECONDARY};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea`
  border: 1px solid #dbe4f2;
  border-radius: 0.5rem;
  padding: 0.75rem;
  font-family: ${FONTS.SECONDARY};
  font-size: 1rem;
  color: ${COLORS.SECONDARY};
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #ff7057;
    box-shadow: 0 0 0 3px rgba(255, 112, 87, 0.1);
  }

  &::placeholder {
    color: #a0a8b8;
  }
`;

const Input = styled.input`
  border: 1px solid #dbe4f2;
  border-radius: 0.5rem;
  padding: 0.75rem;
  font-family: ${FONTS.SECONDARY};
  font-size: 1rem;
  color: ${COLORS.SECONDARY};
  transition: border-color 0.2s;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #ff7057;
    box-shadow: 0 0 0 3px rgba(255, 112, 87, 0.1);
  }

  &::placeholder {
    color: #a0a8b8;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
`;

const PrimaryButton = styled.button`
  background-color: #ff7057;
  color: white;
  border: none;
  border-radius: 1.5rem;
  padding: 0.75rem 2rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  min-width: 140px;

  &:hover {
    background-color: #e55a41;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 112, 87, 0.3);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: ${COLORS.SECONDARY};
  border: 1px solid #dbe4f2;
  border-radius: 1.5rem;
  padding: 0.75rem 2rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;

  &:hover {
    background-color: #f5f8fd;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(171, 185, 207, 0.3);
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-top: 1rem;
  color: #d32f2f;
  font-family: ${FONTS.SECONDARY};
  font-size: 0.9rem;
  text-align: center;
`;

const SigningAddressModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  offer,
  isSubmitting = false,
  error = null
}) => {
  const [offerorAddress, setOfferorAddress] = useState('');
  const [counterpartyAddress, setCounterpartyAddress] = useState('');
  const [counterpartyDni, setCounterpartyDni] = useState('');
  const [lawyerDni, setLawyerDni] = useState('');

  // Determine if lawyer DNI field should be visible
  const hasLawyer = offer && (
    offer.lawyer?.firstName || 
    offer.lawyer?.lastNames || 
    offer.lawyer?.idValue ||
    offer.lawyer_name || 
    offer.lawyer_last_name ||
    offer.lawyer_id ||
    offer.representacionContraparte?.activa
  );

  // Autopopulate fields when offer data changes
  useEffect(() => {
    if (offer && isOpen) {
      // Set counterparty address
      setCounterpartyAddress(offer?.counterparty?.address || '');
      
      // Autopopulate counterparty DNI if available
      const counterpartyId = offer.counterparty?.idValue || '';
      setCounterpartyDni(counterpartyId);

      // Autopopulate lawyer DNI if available and lawyer exists
      if (hasLawyer) {
        const lawyerId = offer.lawyer?.idValue || 
                        offer.lawyer?.identityNumber || 
                        offer.lawyer?.identificationNumber ||
                        offer.lawyer_id ||
                        offer.representacionContraparte?.identificacion ||
                        '';
        setLawyerDni(lawyerId);
      }
    }
  }, [offer, hasLawyer, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOfferorAddress('');
      setCounterpartyAddress('');
      setCounterpartyDni('');
      setLawyerDni('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!offerorAddress.trim() || 
        !counterpartyAddress.trim() || 
        !counterpartyDni.trim() || 
        (hasLawyer && !lawyerDni.trim())) {
      return;
    }

    onSubmit({
      offerorAddress: offerorAddress.trim(),
      counterpartyAddress: counterpartyAddress.trim(),
      counterpartyDni: counterpartyDni.trim(),
      lawyerDni: hasLawyer ? lawyerDni.trim() : null
    });
  };

  // Validation logic - all required fields must be filled
  const isFormValid = offerorAddress?.trim() && 
                     counterpartyAddress?.trim() && 
                     counterpartyDni?.trim() && 
                     (!hasLawyer || lawyerDni?.trim());

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <SigningIcon>✓</SigningIcon>
        </ModalHeader>
        
        <ModalTitle>Firmar Acuerdo Transaccional</ModalTitle>
        
        <ModalMessage>
          Para proceder con la firma electrónica del acuerdo, necesitamos las direcciones de domicilio y documentos de identificación de ambas partes.
        </ModalMessage>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="offerorAddress">
              Domicilio de la Parte Ofertante *
            </Label>
            <TextArea
              id="offerorAddress"
              value={offerorAddress}
              onChange={(e) => setOfferorAddress(e.target.value)}
              placeholder="Introduce la dirección completa incluyendo calle, número, ciudad, código postal y país"
              required
              disabled={isSubmitting}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="counterpartyAddress">
              Domicilio de la Contraparte *
            </Label>
            <TextArea
              id="counterpartyAddress"
              value={counterpartyAddress}
              onChange={(e) => setCounterpartyAddress(e.target.value)}
              placeholder="Introduce la dirección completa incluyendo calle, número, ciudad, código postal y país"
              required
              disabled={isSubmitting}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="counterpartyDni">
              DNI/NIE de la Contraparte *
            </Label>
            <Input
              id="counterpartyDni"
              type="text"
              value={counterpartyDni}
              onChange={(e) => setCounterpartyDni(e.target.value)}
              placeholder="Introduzca el DNI/NIE de la contraparte"
              required
              disabled={isSubmitting}
            />
          </FormGroup>

          {hasLawyer && (
            <FormGroup>
              <Label htmlFor="lawyerDni">
                DNI/NIE del Representante Legal *
              </Label>
              <Input
                id="lawyerDni"
                type="text"
                value={lawyerDni}
                onChange={(e) => setLawyerDni(e.target.value)}
                placeholder="Introduzca el DNI/NIE del representante de la contraparte"
                required
                disabled={isSubmitting}
              />
            </FormGroup>
          )}

          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}

          <ButtonContainer>
            <PrimaryButton 
              type="submit" 
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting && <LoadingSpinner />}
              {isSubmitting ? 'Iniciando Firma...' : 'Iniciar Firma'}
            </PrimaryButton>
            <SecondaryButton 
              type="button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </SecondaryButton>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SigningAddressModal;